import { ElementRef, Injectable } from '@angular/core';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { loadImaSdk } from '@alugha/ima';
import { Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { PuzzleGameStateModel } from '../../store/puzzle-game-state.model';
import { PuzzleFrameworkConfigModel } from '../../store/puzzle-framework-config.model';

@Injectable({
  providedIn: 'root',
})
export class ImaAdService {
  private readonly adTagUrl: string;
  public adContainer: ElementRef;

  // Fields for store communication.
  private storeSubscription: Subscription;
  private puzzleGameStateSubscription: Subscription;

  private puzzleID;

  // Fields for IMA
  private adsRequest;
  private adsLoader;
  private adDisplayContainer;
  private adsManager;
  private sessionTimer = {
    currentTime: 0,
  };
  private sessionTimerId;
  private sessionTimerRunning = false;
  // Tracks if the adsManager has been initialized and started on the first page load
  private adsManagerStarted = false;

  private finished: boolean;
  private adsPlaying = false;

  constructor(private framework: PuzzleFrameworkService, private store: Store) {
    // console.log('ImaAdService.constructor', this.framework.puzzleFrameworkConfig);

    // // Wait for config file to be loaded, then if tag is set initialize everything.
    // this.framework.puzzleConfigLoaded$.subscribe((puzzleType) => {
    //
    //   // Load the tcf data, but wait 1s to make sure the config is fully initialized.
    //   // Because if the config is not initialized the tcfV2Enabled will be undefined or false.
    //   // But it might be true in the configOverride which is loaded later.
    //   // So to not accidentally load before tcf stuff, we wait 1s.
    //   setTimeout(() => {
    //     console.log("waited 1s");
    //     this.framework.getTcfConsentData();
    //
    //     // Listen for the result of loading the TCF data
    //     this.framework.tcfConsentLoaded$.subscribe((tcfV2Loaded) => {
    //       if (tcfV2Loaded) {
    //         void this.digestConsent();
    //       } else if (this.framework.puzzleFrameworkConfig.tcfV2Enabled === false) {
    //         console.log('tcfV2Enabled is false, and the result is false');
    //         void this.digestConsent();
    //       } else {
    //         console.log('TCF V2 is enabled, but the result is false');
    //       }
    //     });
    //
    //   }, 1000);
    //   // this.framework.getTcfConsentData();
    //
    //   // console.log("enabled", this.framework.puzzleFrameworkConfig.tcfV2Enabled);
    //   // console.log("config", this.framework.puzzleFrameworkConfig);
    //
    //
    // });

    // Load the tcf data, but wait 1s to make sure the config is fully initialized.
    // Because if the config is not initialized the tcfV2Enabled will be undefined or false.
    // But it might be true in the configOverride which is loaded later.
    // So to not accidentally load before tcf stuff, we wait 1s.
    setTimeout(() => {
      this.store
        .select((state) => state.puzzleFrameworkConfig)
        .subscribe((puzzleFrameworkConfig: PuzzleFrameworkConfigModel) => {
          if (puzzleFrameworkConfig.tcfV2Enabled != null && this.framework.getCurrentAdTagUrl()) {
            // Now the framework config has loaded the value we are looking for and we have an adTagUrl.
            // console.log('TCF V2 is loaded and', puzzleFrameworkConfig.tcfV2Enabled);
            void this.digestConsent();
          }
        });
    }, 1000);
  }

  private async digestConsent() {
    try {
      await loadImaSdk();
      this.setUpIMA();
    } catch (e) {
      console.log('IMA SDK could not be loaded. Check your ad blocker!');
    }
  }

  /**
   * Set up the IMA adsLoader and requests the ads.
   */
  private setUpIMA() {
    // console.log('setUpIMA');

    // Create the ad display container.
    this.adDisplayContainer = new google.ima.AdDisplayContainer(
      this.adContainer.nativeElement,
      this.sessionTimer as HTMLVideoElement // This does not seem to be necessary.
    );

    // Request video ads.
    this.adsRequest = new google.ima.AdsRequest();
    this.adsRequest.adTagUrl = this.framework.getCurrentAdTagUrl();

    // this.adsRequest.setAdWillAutoPlay(false);
    // this.adsRequest.setAdWillPlayMuted(false);

    // Specify the linear and nonlinear slot sizes. This helps the SDK to
    // select the correct creative if multiple are returned.
    this.adsRequest.linearAdSlotWidth = document.getElementById('app-root').offsetWidth; // 640;
    this.adsRequest.linearAdSlotHeight = document.getElementById('app-root').offsetHeight; // 400;
    this.adsRequest.nonLinearAdSlotWidth = document.getElementById('app-root').offsetWidth; // 640;
    this.adsRequest.nonLinearAdSlotHeight = document.getElementById('app-root').offsetHeight / 3; // 150;

    // Create AdsLoader.
    this.adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);

    // Listen and respond to ads loaded and error events.
    this.adsLoader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.onAdsManagerLoaded,
      false
    );
    this.adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, this.onAdsLoaderError, false);

    // Call AdsLoader, pass this service as context so the callback can assign it's stuff.
    this.adsLoader.requestAds(this.adsRequest, { imaAdService: this });
  }

  /**
   * Called if the AdsLoader has a Problem.
   */
  public onAdsLoaderError(adErrorEvent) {
    console.log('Ads Loader has the following problem:', adErrorEvent.getError());
    if (this.adContainer) {
      // this.adContainer.nativeElement.style.display = 'none';
    }
    if (this.adsManager !== undefined) {
      this.adsManager.destroy();
    }
  }

  /**
   * Callen when the AdsLoader successfully loaded the AdsManager.
   */
  public onAdsManagerLoaded(adsManagerLoadedEvent) {
    // console.log('onAdsManagerLoaded');

    const adsRenderingSettings = new google.ima.AdsRenderingSettings();
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

    adsRenderingSettings.enablePreloading = true;

    // Show the ad timer in the lower left corner.
    adsRenderingSettings.uiElements = [google.ima.UiElements.AD_ATTRIBUTION, google.ima.UiElements.COUNTDOWN];

    // Render linear ads with full UI styling. This setting does not apply to AdSense/AdX ads or ads played in a mobile
    // context that already use full UI styling by default.
    // adsRenderingSettings.useStyledLinearAds = true;

    // Use the adsManager and service passed via context.
    const context = adsManagerLoadedEvent.getUserRequestContext();
    const service = context.imaAdService;

    // Get the AdsManager.
    const adsManager = adsManagerLoadedEvent.getAdsManager(service.sessionTimer, adsRenderingSettings);

    // Add our service callback event listeners to the AdsManager.
    adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
      service.onContentPauseRequested();
    });
    adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
      service.onContentResumeRequested();
    });
    adsManager.addEventListener(google.ima.AdEvent.Type.LOADED, (adEvent) => {
      service.onAdLoaded(adEvent);
    });
    adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (adErrorEvent) => {
      service.onAdError(adErrorEvent);
    });

    // Listen to any additional events, if necessary.
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.STARTED,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.COMPLETE,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.CLICK,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.PAUSED,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.RESUMED,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.AD_PROGRESS,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.IMPRESSION,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.DURATION_CHANGE,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.USER_CLOSE,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.SKIPPED,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.LINEAR_CHANGED,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.INTERACTION,
    //   adEvent => { service.onAdEvent(adEvent); });
    // adsManager.addEventListener(
    //   google.ima.AdEvent.Type.AD_BUFFERING,
    //   adEvent => { service.onAdEvent(adEvent); });

    // Assign the newly created AdsManager back to our service.
    context.imaAdService.adsManager = adsManager;

    // Now we can setup the store subscription to listen to and send events.
    service.setupStoreSubscription();
  }

  /**
   * Sets up the store subscription that handles the events.
   * Will run and stop the session timer depending on the pause state.
   * If game ist started it also starts the adsManager, so this needs to be called after loading the adsManager.
   */
  private setupStoreSubscription() {
    // console.log('setupStoreSubscription');
    // Setup communication with the store to send and receive puzzle states
    this.storeSubscription = this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleID) => {
        if (puzzleID === '' || puzzleID === undefined) {
          return;
        }

        this.puzzleID = puzzleID;

        if (this.puzzleGameStateSubscription) {
          this.puzzleGameStateSubscription.unsubscribe();
        }

        this.puzzleGameStateSubscription = this.store
          .select((state) => state.puzzleGameState)
          .subscribe((puzzleGameState: PuzzleGameStateModel) => {
            if (!puzzleGameState) {
              return;
            }

            const puzzleGame = puzzleGameState.puzzleGames.hasOwnProperty(this.puzzleID)
              ? puzzleGameState.puzzleGames[this.puzzleID]
              : null;

            if (!puzzleGame) {
              return;
            }

            // const adsPlaying = puzzleGame.started && !puzzleGame.finished && puzzleGameState.paused <= 0;
            const adsPlaying = !puzzleGame.finished && puzzleGameState.paused <= 0;

            if (adsPlaying !== this.adsPlaying) {
              this.adsPlaying = adsPlaying;
              if (this.adsPlaying) {
                if (!this.adsManagerStarted) {
                  this.startAdsManager();
                }
                this.runTimer();
              } else {
                this.pauseTimer();
              }
            }

            // If puzzleGame just switched to finished
            if (puzzleGame.finished && this.finished !== false) {
              this.gameFinished();
            }
            this.finished = puzzleGame.finished;
          });
      });
  }

  /**
   * Called when the AdsManager wants to play an Ad.
   */
  public onContentPauseRequested() {
    // console.log('onContentPauseRequested');
    this.adContainer.nativeElement.style.display = 'flex';
    this.framework.pauseCurrentGame();
  }

  /**
   * Called when the AdsManager has finished playing an Ad.
   */
  public onContentResumeRequested() {
    // console.log('onContentResumeRequested');
    this.adContainer.nativeElement.style.display = 'none';
    this.framework.resumeCurrentGame();
  }

  /**
   * Called when the AdsManager has loaded an Ad.
   */
  public onAdLoaded(adEvent) {
    // console.log('onAdLoaded');

    // Do stuff for non-linear ads, but they are not supported yet.
    if (adEvent.type === google.ima.AdEvent.Type.LOADED && !adEvent.getAd().isLinear()) {
      this.adContainer.nativeElement.style.display = 'flex';
      this.adContainer.nativeElement.style.background = 'none';
      // Does not work, find a solution if necessary.
      // Somehow none of these work: https://stackoverflow.com/q/24276733/4396362
      this.adContainer.nativeElement.style.pointerEvent = 'none';
      // console.log('Inline Ad is loaded.');
    }
  }

  /**
   * Called when the AdsManager has trouble playing an Ad.
   */
  public onAdError(adErrorEvent) {
    console.log('AdsManager could not play ads', adErrorEvent.getError());
    if (this.adContainer) {
      this.adContainer.nativeElement.style.display = 'none';
      this.framework.resumeCurrentGame();
    }
    if (this.adsManager !== undefined) {
      this.adsManager.destroy();
    }
  }

  /**
   * Listen to Ad Events. Currently not used.
   */
  public onAdEvent(adEvent) {
    // console.log('onAdEvent', adEvent);
    //   // Retrieve the ad from the event. Some events (e.g. ALL_ADS_COMPLETED) don't have ad object associated.
    //   const ad = adEvent.getAd();
    //   // console.log('ad', ad);
    //   // console.log('adData', adEvent.getAdData());
    //   switch (adEvent.type) {
    //   //   case google.ima.AdEvent.Type.LOADED:
    //   //     // This is the first event sent for an ad - it is possible to
    //   //     // determine whether the ad is a video ad or an overlay.
    //   //     console.log('AdEvent.Type.LOADED');
    //   //     if (!ad.isLinear()) {
    //   //       // Position AdDisplayContainer correctly for overlay.
    //   //       // Use ad.width and ad.height.
    //   //       // videoContent.play();
    //   //     }
    //   //     break;
    //   //   case google.ima.AdEvent.Type.STARTED:
    //   //     // This event indicates the ad has started - the video player
    //   //     // can adjust the UI, for example display a pause button and
    //   //     // remaining time.
    //   //     console.log('AdEvent.Type.STARTED');
    //   //     if (ad.isLinear()) {
    //   //       // For a linear ad, a intervalId can be started to poll for
    //   //       // the remaining time.
    //   //       // intervalTimer = window.setInterval(
    //   //       window.setInterval(
    //   //         (adsManager) => {
    //   //           const remainingTime = adsManager.getRemainingTime();
    //   //           console.log('Ad running for another', remainingTime, 'seconds.');
    //   //         },
    //   //         300, this.adsManager); // every 300ms
    //   //     }
    //   //     break;
    //   //   case google.ima.AdEvent.Type.COMPLETE:
    //   //     // This event indicates the ad has finished - the video player
    //   //     // can perform appropriate UI actions, such as removing the intervalId for
    //   //     // remaining time detection.
    //   //     console.log('AdEvent.Type.COMPLETE');
    //   //     if (ad.isLinear()) {
    //   //       // clearInterval(intervalTimer);
    //   //     }
    //   //     break;
    //   }
  }

  /**
   * Initializes and starts the adsManager.
   */
  public startAdsManager() {
    // console.log('startAdsManager', this.adsManager);
    if (this.adsManager) {
      this.adDisplayContainer.initialize();
      this.adsManager.init(
        document.getElementById('app-root').offsetWidth, // 640,
        document.getElementById('app-root').offsetHeight, // 360,
        google.ima.ViewMode.NORMAL
      );
      this.adsManager.start();
      this.adsManagerStarted = true;
    } else {
      console.log('Could not play ads, resume to normal');
      this.adContainer.nativeElement.style.display = 'none';
    }
  }

  /**
   * Needs to be called when game is finished to call postroll add.
   */
  private gameFinished() {
    // console.log('gameFinished');
    this.adsLoader.contentComplete();
  }

  /**
   * Runs the internal session timer.
   */
  private runTimer() {
    if (!this.sessionTimerRunning) {
      this.sessionTimerId = window.setInterval(() => {
        // console.log('sessionTimer.currentTime:', this.sessionTimer.currentTime);
        this.sessionTimer.currentTime++;
      }, 1000);
      this.sessionTimerRunning = true;
    }
  }

  /**
   * Stops the internal session timer.
   */
  private pauseTimer() {
    if (this.sessionTimerId) {
      window.clearInterval(this.sessionTimerId);
      this.sessionTimerRunning = false;
    }
  }
}
